import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Capacitor} from '@capacitor/core';
import {Preferences} from '@capacitor/preferences';

import {User} from '../_models/user';
import {environment} from 'src/environments/environment';
import { posSyncDb } from './pos-sync-db';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    // Heartbeat key kept in sessionStorage. sessionStorage is wiped when the
    // browser/tab is closed but survives reloads — so its presence tells us the
    // current browser session is still alive.
    private static readonly SESSION_ACTIVE_KEY = 'sessionActive';

    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    private isNative = Capacitor.isNativePlatform();

    constructor(private http: HttpClient) {
        this.currentUserSubject = new BehaviorSubject<User>(this.getValidStoredUser());
        this.currentUser = this.currentUserSubject.asObservable();

        // For native platforms, also load from Preferences (more reliable)
        if (this.isNative) {
            this.loadUserFromPreferences();
        }
    }

    // Load user from Capacitor Preferences (async, for native)
    private async loadUserFromPreferences() {
        try {
            const { value } = await Preferences.get({ key: 'currentUser' });
            if (value) {
                const user = JSON.parse(value);
                // Also sync to localStorage for compatibility
                localStorage.setItem('currentUser', value);
                this.currentUserSubject.next(user);
            }
        } catch (e) {
            console.error('Error loading user from Preferences:', e);
        }
    }

    // Save to both localStorage and Preferences (for native)
    private async saveUser(user: User, rememberMe: boolean) {
        const userJson = JSON.stringify(user);
        localStorage.setItem('currentUser', userJson);
        localStorage.setItem('remember', rememberMe.toString());

        if (this.isNative) {
            try {
                await Preferences.set({ key: 'currentUser', value: userJson });
                await Preferences.set({ key: 'remember', value: rememberMe.toString() });
            } catch (e) {
                console.error('Error saving to Preferences:', e);
            }
        }
    }

    // Save credentials for auto re-login (native only, with basic encoding)
    private async saveCredentials(username: string, password: string) {
        if (this.isNative) {
            try {
                // Basic encoding (not secure encryption, but better than plaintext)
                const encoded = btoa(JSON.stringify({ u: username, p: password }));
                await Preferences.set({ key: 'credentials', value: encoded });
            } catch (e) {
                console.error('Error saving credentials:', e);
            }
        }
    }

    // Get saved credentials for auto re-login
    public async getSavedCredentials(): Promise<{ username: string; password: string } | null> {
        if (this.isNative) {
            try {
                const { value } = await Preferences.get({ key: 'credentials' });
                if (value) {
                    const decoded = JSON.parse(atob(value));
                    return { username: decoded.u, password: decoded.p };
                }
            } catch (e) {
                console.error('Error getting credentials:', e);
            }
        }
        return null;
    }

    // Clear saved credentials
    private async clearCredentials() {
        if (this.isNative) {
            try {
                await Preferences.remove({ key: 'credentials' });
            } catch (e) {
                console.error('Error clearing credentials:', e);
            }
        }
    }

    // Auto re-login using saved credentials (for native when token expired)
    public async tryAutoReLogin(): Promise<boolean> {
        if (!this.isNative) return false;

        const credentials = await this.getSavedCredentials();
        if (!credentials) return false;

        try {
            const response = await this.http.post<any>(
                `${environment.apiUrl}/backend/api/token/`,
                { username: credentials.username, password: credentials.password }
            ).toPromise();

            if (response && response.access) {
                const token_parts = response.access.split(/\./);
                const token_decoded = JSON.parse(window.atob(token_parts[1]));
                const user = new User(
                    token_decoded.id,
                    token_decoded.username,
                    token_decoded.first_name,
                    token_decoded.last_name,
                    token_decoded.last_login,
                    response.access
                );
                await this.saveUser(user, true);
                this.currentUserSubject.next(user);
                return true;
            }
        } catch (e) {
            console.error('Auto re-login failed:', e);
            // Clear invalid credentials
            await this.clearCredentials();
        }
        return false;
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    /** True when there is a stored user whose token has not expired. */
    public isLoggedIn(): boolean {
        const user = this.currentUserValue;
        return !!(user && user.token && !this.isTokenExpired(user.token));
    }

    // Check if remember me is enabled (async for native)
    public async isRememberMeEnabled(): Promise<boolean> {
        if (this.isNative) {
            try {
                const { value } = await Preferences.get({ key: 'remember' });
                return value === 'true';
            } catch (e) {
                return localStorage.getItem('remember') === 'true';
            }
        }
        return localStorage.getItem('remember') === 'true';
    }

    /**
     * Resolve the user to start the app with. Returns null (and clears storage)
     * when the session is missing, expired, or was a non-remembered session
     * that should not survive a browser restart.
     */
    private getValidStoredUser(): User {
        let user: User = null;
        try {
            user = JSON.parse(localStorage.getItem('currentUser'));
        } catch {
            user = null;
        }

        if (!user || !user.token) {
            this.clearSession();
            return null;
        }

        // Expired token -> drop the session.
        if (this.isTokenExpired(user.token)) {
            this.clearSession();
            return null;
        }

        // "Keep me signed in" was not checked: the session is only valid while
        // the browser session lives. If the heartbeat is gone, the browser was
        // closed and reopened, so this session must not be restored.
        const remembered = localStorage.getItem('remember') === 'true';
        const sessionAlive = sessionStorage.getItem(AuthenticationService.SESSION_ACTIVE_KEY) === 'true';
        if (!remembered && !sessionAlive) {
            this.clearSession();
            return null;
        }

        // Valid session — (re)arm the heartbeat for this browser session.
        sessionStorage.setItem(AuthenticationService.SESSION_ACTIVE_KEY, 'true');
        return user;
    }

    /** Decode the payload of a JWT, or null if it cannot be parsed. */
    private decodeToken(token: string): any {
        try {
            const payload = token.split('.')[1];
            const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
            return JSON.parse(window.atob(normalized));
        } catch {
            return null;
        }
    }

    /**
     * True when the token is missing an exp claim or is past expiry. Defaults to
     * the current user's token when no token is supplied (used by the native
     * auto-login flow).
     */
    public isTokenExpired(token?: string): boolean {
        const t = token ?? this.currentUserValue?.token;
        if (!t) {
            return true;
        }
        const decoded = this.decodeToken(t);
        if (!decoded || !decoded.exp) {
            return true;
        }
        // exp is in seconds since epoch.
        return decoded.exp * 1000 <= Date.now();
    }

    /** Remove all auth state from local and session storage. */
    private clearSession() {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('remember');
        sessionStorage.removeItem(AuthenticationService.SESSION_ACTIVE_KEY);
    }

    login(username: string, password: string, rememberMe:boolean) {
      this.prepareForLogin();

      return this.http.post<any>(`${environment.apiUrl}/backend/api/token/`, { username, password })
      .pipe(map(response => {
          // login successful if there's a jwt token in the response
          if (response && response.access) {
            const user = this.persistSession(response.access, rememberMe);
            // Native: also mirror to Preferences and save credentials so the
            // PAX terminal can auto re-login after a token expires.
            if (this.isNative) {
              this.saveUser(user, rememberMe);
              if (rememberMe) {
                this.saveCredentials(username, password);
              }
            }
          }
          return response;
      }));
    }

    /**
     * "Continue with Google" sign-in. Exchanges a Google ID token for the same
     * SimpleJWT access token password login uses, then persists it identically.
     */
    googleLogin(idToken: string, rememberMe: boolean = true) {
      this.prepareForLogin();

      return this.http.post<any>(`${environment.apiUrl}/backend/api/google-auth/`, { id_token: idToken })
      .pipe(map(response => {
          if (response && response.access) {
            this.persistSession(response.access, rememberMe);
          }
          return response;
      }));
    }

    /**
     * Step 1 of phone sign-in (ENG-5362): ask the backend to text a passcode.
     * Deliberately says nothing about whether the number is on an account.
     */
    requestLoginOtp(phoneNumber: string) {
      return this.http.post<any>(`${environment.apiUrl}/backend/api/phone-otp/request/`,
        { phone_number: phoneNumber });
    }

    /**
     * Step 2 of phone sign-in: trade the passcode for an access token. The
     * backend returns the same token password login does, so the session that
     * comes out of this is indistinguishable downstream.
     */
    verifyLoginOtp(phoneNumber: string, otp: string, rememberMe: boolean = false) {
      this.prepareForLogin();

      return this.http.post<any>(`${environment.apiUrl}/backend/api/phone-otp/verify/`,
        { phone_number: phoneNumber, otp })
      .pipe(map(response => {
          if (response && response.access) {
            const user = this.persistSession(response.access, rememberMe);
            // Native mirrors the session like password login does. No
            // credentials are saved: there is no reusable secret to store, so
            // a PAX terminal signed in this way cannot silently re-login and
            // will ask for a fresh code once the token expires.
            if (this.isNative) {
              this.saveUser(user, rememberMe);
            }
          }
          return response;
      }));
    }

    /** Reset local state before a fresh login (shared by all login methods). */
    private prepareForLogin() {
      const mqttSession = localStorage.getItem('mqttsession');
      // ENG-5148: keep the cached POS display size across the wipe so the UI
      // doesn't flash back to default before the store-wide value loads.
      const posUiScale = localStorage.getItem('posUiScale');
      localStorage.clear();
      if (mqttSession) {
        localStorage.setItem('mqttsession', mqttSession);
      } else {
        localStorage.setItem('mqttsession', Date.now().toString());
      }
      if (posUiScale) {
        localStorage.setItem('posUiScale', posUiScale);
      }

      // ENG-4644: Clear all Dexie caches so stale settings/menu data from
      // the previous session don't leak into the new login.
      posSyncDb.store_config.clear().catch(() => {});
      posSyncDb.menu_cache.clear().catch(() => {});
      posSyncDb.items_cache.clear().catch(() => {});
      posSyncDb.modifiers_cache.clear().catch(() => {});
    }

    /** Store the authenticated user + remember-me state from an access token. */
    private persistSession(accessToken: string, rememberMe: boolean): User {
      const decoded = this.decodeToken(accessToken) || {};
      const user = new User(decoded.id, decoded.username, decoded.first_name,
        decoded.last_name, decoded.last_login, accessToken);
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('remember', rememberMe.toString());
      // Arm the browser-session heartbeat so a non-remembered session
      // stays valid across reloads but ends when the browser closes.
      sessionStorage.setItem(AuthenticationService.SESSION_ACTIVE_KEY, 'true');
      this.currentUserSubject.next(user);
      return user;
    }

    async logout() {
        // remove user from local storage to log user out
      this.clearSession();

      // Also clear from Preferences for native (including saved credentials)
      if (this.isNative) {
          try {
              await Preferences.remove({ key: 'currentUser' });
              await Preferences.remove({ key: 'remember' });
              await Preferences.remove({ key: 'credentials' });
          } catch (e) {
              console.error('Error removing from Preferences:', e);
          }
      }

      this.currentUserSubject.next(null);
    }


    resetPassword(email:string){
      return this.http.post<any>(`${environment.apiUrl}/backend/rest-auth/password/reset/`, {email})
    }

    resetPasswordConfirm(uid:string, token:string, new_password1:string, new_password2:string){
      return this.http.post<any>(`${environment.apiUrl}/backend/rest-auth/password/reset/confirm/`, {uid, token, new_password1, new_password2})
    }

    changePassword(new_password1, new_password2){
      return this.http.post<any>(`${environment.apiUrl}/backend/rest-auth/password/change/`, {new_password1, new_password2})
    }

    isFirstLogin(){
      return this.http.get<any>(`${environment.apiUrl}/backend/rest-auth/user/`)
    }
}
